import { Fragment, useEffect, useRef, useState } from 'react'
import { useOutsideClick } from '../../hooks'
import { TEXT_AREA_ROWS_MIN } from '../../utils/constants'
import { formatNum, parseDate, setToCalendarDate } from '../../utils/functions'
import { QuestionSchema, QuestionTypeSchema, SectionSchema } from '../../utils/types/form'
import questionTypes from '../../data/question-types.json'

const newQuestion = { type: 'text', required: false } as QuestionSchema

export const QuestionWizard: React.FC<{
  section: SectionSchema
  question?: QuestionSchema
  newQuestionIndex: number
  save: (question: QuestionSchema, sectionId: string, questionIndex: number) => void
  reset: () => void
}> = (props) => {
  const { question: editQuestion, newQuestionIndex, section, save, reset } = props

  const [question, setQuestion] = useState(editQuestion ?? newQuestion)
  const [questionIndex, setQuestionIndex] = useState(newQuestionIndex)
  const [isQuestionTypesOpen, setIsQuestionTypesOpen] = useState(false)
  const [isNewOptionField, setIsNewOptionField] = useState(true)
  const [optionValue, setOptionValue] = useState('')
  const [questionSetting, setQuestionSetting] = useState({
    insert: false,
    unit: false,
    min: false,
    max: false,
    minlength: false,
    maxlength: false,
    minOptions: false,
    maxOptions: false,
    spellcheck: false,
    placeholder: false
  })
  const [validationError, setValidationError] = useState('')
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null)
  const maxValue = useRef(section.questions.length + +`${editQuestion ? 0 : 1}`)
  const questionTypesTriggerRef = useRef(null)
  const questionTypesContextRef = useRef(null)

  useOutsideClick({ element: questionTypesContextRef, trigger: questionTypesTriggerRef }, () =>
    setIsQuestionTypesOpen(false)
  )

  useEffect(() => {
    setFocus(textAreaRef)
  }, [textAreaRef])

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => setQuestion({ ...question, [e.target.id]: e.target.value })

  const handleTypeChange = (e: React.ChangeEvent<HTMLInputElement | HTMLLabelElement>) => {
    let meta = question.meta

    if (e.target.id === 'checkbox' && question.meta?.dropdownOptions) {
      meta = { checkboxOptions: question.meta.dropdownOptions }
    }

    if (e.target.id === 'dropdown' && question.meta?.checkboxOptions) {
      meta = { dropdownOptions: question.meta.checkboxOptions }
    }

    setQuestion({ ...question, type: e.target.id as QuestionTypeSchema, meta })
    setIsQuestionTypesOpen(false)
  }

  const handleQuestionIndex = (e: React.ChangeEvent<HTMLInputElement>) =>
    setQuestionIndex(Number(e.target.value) - 1)

  const handleNewQuestionOptionChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setOptionValue(e.target.value)

  const handleCheckboxChange = () => setQuestion({ ...question, required: !question.required })

  const handleNewOption = () => {
    setOptionValue('')
    setIsNewOptionField(true)
  }

  const handleNewQuestionTextOptions = (e: React.ChangeEvent<HTMLInputElement>) => {
    const maxValue = question.meta?.textOptions?.maxlength
    const minValue = question.meta?.textOptions?.minlength

    if (
      (e.target.id === 'minlength' &&
        maxValue !== undefined &&
        e.target.value &&
        +e.target.value > maxValue) ||
      (e.target.id === 'maxlength' &&
        minValue !== undefined &&
        e.target.value &&
        +e.target.value < minValue)
    ) {
      setValidationError('MIN cannot be greater than MAX')
    } else {
      setValidationError('')
    }

    if (e.target.value) {
      setQuestion({
        ...question,
        meta: {
          ...question.meta,
          textOptions: {
            ...question.meta?.textOptions,
            [e.target.id]: +e.target.value
          }
        }
      })
    } else {
      setQuestion((question) => {
        const newQuestion = JSON.parse(JSON.stringify(question))
        delete newQuestion.meta?.textOptions?.[e.target.id]
        return newQuestion
      })
    }
  }

  const handleNewQuestionNumberOptions = (
    e: React.ChangeEvent<HTMLInputElement>,
    isString: Boolean = false
  ) => {
    const maxValue = question.meta?.numberOptions?.max
    const minValue = question.meta?.numberOptions?.min

    if (
      (e.target.id === 'min' &&
        maxValue !== undefined &&
        e.target.value &&
        +e.target.value > maxValue) ||
      (e.target.id === 'max' &&
        minValue !== undefined &&
        e.target.value &&
        +e.target.value < minValue)
    ) {
      setValidationError('MIN cannot be greater than MAX')
    } else {
      setValidationError('')
    }

    if (e.target.value) {
      setQuestion({
        ...question,
        meta: {
          ...question.meta,
          numberOptions: {
            ...question.meta?.numberOptions,
            [e.target.id]: isString ? e.target.value : +e.target.value
          }
        }
      })
    } else {
      setQuestion((question) => {
        const newQuestion = JSON.parse(JSON.stringify(question))
        delete newQuestion.meta?.numberOptions?.[e.target.id]
        return newQuestion
      })
    }
  }

  const handleNewQuestionEmailOptions = (e: React.ChangeEvent<HTMLInputElement>) => {
    const maxValue = question.meta?.emailOptions?.maxlength
    const minValue = question.meta?.emailOptions?.minlength

    if (
      (e.target.id === 'minlength' &&
        maxValue !== undefined &&
        e.target.value &&
        +e.target.value > maxValue) ||
      (e.target.id === 'maxlength' &&
        minValue !== undefined &&
        e.target.value &&
        +e.target.value < minValue)
    ) {
      setValidationError('MIN cannot be greater than MAX')
    } else {
      setValidationError('')
    }

    if (e.target.value) {
      setQuestion({
        ...question,
        meta: {
          ...question.meta,
          emailOptions: {
            ...question.meta?.emailOptions,
            [e.target.id]: e.target.value
          }
        }
      })
    } else {
      setQuestion((question) => {
        const newQuestion = JSON.parse(JSON.stringify(question))
        delete newQuestion.meta?.emailOptions?.[e.target.id]
        return newQuestion
      })
    }
  }

  const handleNewQuestionTelOptions = (e: React.ChangeEvent<HTMLInputElement>) => {
    const maxValue = question.meta?.telOptions?.maxlength
    const minValue = question.meta?.telOptions?.minlength

    if (
      (e.target.id === 'minlength' &&
        maxValue !== undefined &&
        e.target.value &&
        +e.target.value > maxValue) ||
      (e.target.id === 'maxlength' &&
        minValue !== undefined &&
        e.target.value &&
        +e.target.value < minValue)
    ) {
      setValidationError('MIN cannot be greater than MAX')
    } else {
      setValidationError('')
    }

    if (e.target.value) {
      setQuestion({
        ...question,
        meta: {
          ...question.meta,
          telOptions: {
            ...question.meta?.telOptions,
            [e.target.id]: e.target.value
          }
        }
      })
    } else {
      setQuestion((question) => {
        const newQuestion = JSON.parse(JSON.stringify(question))
        delete newQuestion.meta?.telOptions?.[e.target.id]
        return newQuestion
      })
    }
  }

  const handleNewQuestionDateOptions = (e: React.ChangeEvent<HTMLInputElement>) => {
    let maxValue = question.meta?.dateOptions?.max
    let minValue = question.meta?.dateOptions?.min
    let value: string | number = e.target.value

    if (maxValue) {
      maxValue = new Date(maxValue).getTime()
    }

    if (minValue) {
      minValue = new Date(minValue).getTime()
    }

    if (value) {
      value = new Date(value).getTime()
    }

    if (
      (e.target.id === 'min' && maxValue !== undefined && value && +value > maxValue) ||
      (e.target.id === 'max' && minValue !== undefined && value && +value < minValue)
    ) {
      setValidationError('MIN DATE cannot be greater than MAX DATE')
    } else {
      setValidationError('')
    }

    if (e.target.value) {
      setQuestion({
        ...question,
        meta: {
          ...question.meta,
          dateOptions: {
            ...question.meta?.dateOptions,
            [e.target.id]: e.target.value
          }
        }
      })
    } else {
      setQuestion((question) => {
        const newQuestion = JSON.parse(JSON.stringify(question))
        delete newQuestion.meta?.dateOptions?.[e.target.id]
        return newQuestion
      })
    }
  }

  const handleNewQuestionSelectOptions = (e: React.ChangeEvent<HTMLInputElement>) => {
    const maxValue = question.meta?.checkboxOptions?.maxOptions
    const minValue = question.meta?.checkboxOptions?.minOptions
    const total = question.meta?.checkboxOptions?.values.length

    if (
      (e.target.id === 'minOptions' &&
        maxValue !== undefined &&
        e.target.value &&
        +e.target.value > maxValue) ||
      (e.target.id === 'maxOptions' &&
        minValue !== undefined &&
        e.target.value &&
        +e.target.value < minValue)
    ) {
      setValidationError('MIN cannot be greater than MAX')
    } else if (total !== undefined && +e.target.value > total) {
      setValidationError(`VALUE cannot be greater than total options (${total})`)
    } else {
      setValidationError('')
    }

    if (e.target.value) {
      setQuestion({
        ...question,
        meta: {
          ...question.meta,
          checkboxOptions: {
            ...question.meta?.checkboxOptions,
            [e.target.id]: +e.target.value
          } as {
            values: string[]
            minOptions?: number
            maxOptions?: number
          }
        }
      })
    } else {
      setQuestion((question) => {
        const newQuestion = JSON.parse(JSON.stringify(question))
        delete newQuestion.meta?.checkboxOptions?.[e.target.id]
        return newQuestion
      })
    }
  }

  const handleNewQuestionRatingOptions = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (+e.target.value < 0) {
      setValidationError('MAX cannot be less than 0')
    } else {
      setValidationError('')
    }

    const values = []

    for (let i = +e.target.value; i > 0; i--) {
      values.push(i)
    }

    if (e.target.value) {
      setQuestion({
        ...question,
        meta: {
          ...question.meta,
          ratingOptions: values
        }
      })
    } else {
      setQuestion((question) => {
        const newQuestion = JSON.parse(JSON.stringify(question))
        delete newQuestion.meta?.ratingOptions
        return newQuestion
      })
    }
  }

  const handleNewQuestionPlaceholder = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value) {
      setQuestion({
        ...question,
        meta: {
          ...question.meta,
          placeholder: e.target.value
        }
      })
    } else {
      setQuestion((question) => {
        const newQuestion = JSON.parse(JSON.stringify(question))
        delete newQuestion.meta?.placeholder
        return newQuestion
      })
    }
  }

  const handleNewQuestionDropdownOptions = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value) {
      setQuestion({
        ...question,
        meta: {
          dropdownOptions: {
            ...question.meta?.dropdownOptions,
            values: question.meta?.dropdownOptions?.values
              ? [...question.meta.dropdownOptions.values, optionValue]
              : [optionValue]
          }
        }
      })

      setIsNewOptionField(false)
    }
  }

  const handleNewQuestionCheckboxOptions = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value) {
      setQuestion({
        ...question,
        meta: {
          checkboxOptions: {
            ...question.meta?.checkboxOptions,
            values: question.meta?.checkboxOptions?.values
              ? [...question.meta.checkboxOptions.values, optionValue]
              : [optionValue]
          }
        }
      })

      setIsNewOptionField(false)
    }
  }

  const handleNewQuestionDropdownEdit = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    if (e.target.value && question.meta?.dropdownOptions) {
      const dropdownOptions = JSON.parse(JSON.stringify(question.meta.dropdownOptions))
      dropdownOptions[index] = e.target.value
      setQuestion({ ...question, meta: { dropdownOptions } })
    }
  }

  const handleNewQuestionCheckboxEdit = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    if (e.target.value && question.meta?.checkboxOptions) {
      const checkboxOptions = JSON.parse(JSON.stringify(question.meta.checkboxOptions))

      checkboxOptions[index] = e.target.value
      setQuestion({ ...question, meta: { checkboxOptions } })
    }
  }

  const handleMoveCheckbox = (
    option: string,
    index: number,
    newIndex: number,
    questionOptions: { name: string; values?: string[] }
  ) => {
    const { name, values } = questionOptions

    const options = JSON.parse(JSON.stringify(values))

    options.splice(index, 1)
    options.splice(newIndex, 0, option)

    setQuestion({ ...question, meta: { [name]: { values: options } } })
  }

  const handleNewQuestionDropdownDelete = (index: number) => {
    if (question.meta?.dropdownOptions) {
      const dropdownOptions = JSON.parse(JSON.stringify(question.meta.dropdownOptions))
      dropdownOptions.values.splice(index, 1)
      setQuestion({ ...question, meta: { dropdownOptions } })
    }
  }

  const handleNewQuestionCheckboxDelete = (index: number) => {
    if (question.meta?.checkboxOptions) {
      const checkboxOptions = JSON.parse(JSON.stringify(question.meta.checkboxOptions))
      checkboxOptions.values.splice(index, 1)
      setQuestion({ ...question, meta: { checkboxOptions } })
    }
  }

  /* const handleNewQuestionSpellcheck = () => {
    setQuestion({
      ...question,
      meta: {
        ...question.meta,
        textOptions: {
          ...question.meta?.textOptions,
          spellcheck: !question.meta?.textOptions?.spellcheck
        }
      }
    })
  } */

  /* const handleNewQuestionConditionSelect = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    if (e.target.value) {
      setQuestion({
        ...question,
        conditional: {
          ...question.conditional,
          [e.target.id]: e.target.value
        } as ConditionalSchema
      })
    }
  } */

  const setFocus = (ref: React.RefObject<HTMLTextAreaElement | null>) => {
    const element = ref.current

    if (element) {
      element.focus()
      element.setSelectionRange(element.value.length, element.value.length)
    }
  }

  return (
    <div className='section-new_item section-new_item-mt'>
      <h3>{`${editQuestion ? 'Edit' : 'New'}`} question</h3>
      <div className='section_question'>
        <span />
        <div className='section_new_questions'>
          <div />
          <div className='actions_container actions_container-alt'>
            <div className='actions-select actions-select-fg'>
              <span className='material-symbols-rounded material-symbols-sm'>
                format_list_bulleted
              </span>
              <div className='actions_series'>
                <div onClick={() => setQuestionSetting({ ...questionSetting, insert: true })}>
                  <div className='div-with-icon div-with-icon-pd'>
                    <span>Number:</span>
                    {questionSetting.insert ? (
                      <input
                        type='number'
                        defaultValue={questionIndex + 1}
                        onBlur={(e) => {
                          handleQuestionIndex(e)
                          setQuestionSetting({
                            ...questionSetting,
                            insert: false
                          })
                        }}
                        min={1}
                        max={maxValue.current}
                        autoFocus
                        placeholder='--'
                      />
                    ) : (
                      <span className='span-input'>{formatNum(questionIndex + 1)}</span>
                    )}
                  </div>
                </div>
              </div>
              <div className='actions-options'>
                <div className='checkbox_container checkbox_container-alt'>
                  <div>
                    <input
                      id='required'
                      type='checkbox'
                      checked={question.required}
                      onChange={handleCheckboxChange}
                      hidden
                    />
                    <span
                      className={`material-symbols-rounded ${question.required ? 'on' : ''}`}
                      onClick={handleCheckboxChange}>
                      {question.required ? 'toggle_on' : 'toggle_off'}
                    </span>
                    <label htmlFor='required'>Required</label>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <label htmlFor='statement'>
            Question:<em className='required'>*</em>
          </label>
          <textarea
            id='statement'
            defaultValue={question.statement}
            onChange={handleChange}
            rows={TEXT_AREA_ROWS_MIN}
            placeholder='Enter question...'
            ref={textAreaRef}
          />
          <label htmlFor='description'>Description:</label>
          <textarea
            id='description'
            defaultValue={question?.description}
            onChange={handleChange}
            rows={TEXT_AREA_ROWS_MIN}
            placeholder='Description (optional)...'
          />
          <label onClick={() => setIsQuestionTypesOpen(true)}>Type:</label>
          <div className={`select-custom ${isQuestionTypesOpen ? 'select-custom-focus' : ''}`}>
            <div
              id='question_types_trigger'
              className='input_container'
              onClick={() => setIsQuestionTypesOpen(!isQuestionTypesOpen)}
              ref={questionTypesTriggerRef}>
              <span id='question_types_trigger'>
                {questionTypes.find(({ type }) => type === question.type)?.name}
              </span>
              <span id='question_types_trigger' className='material-symbols-rounded'>
                {isQuestionTypesOpen ? 'arrow_drop_up' : 'arrow_drop_down'}
              </span>
            </div>
            {isQuestionTypesOpen && (
              <div
                className='context_menu context_menu-down context_menu-grid context_menu-grid-alt'
                ref={questionTypesContextRef}>
                {questionTypes.map((questionType) => (
                  <Fragment key={questionType.type}>
                    <input
                      id={questionType.type}
                      type='checkbox'
                      onChange={handleTypeChange}
                      hidden
                    />
                    <label
                      htmlFor={questionType.type}
                      className={`${questionType.type === question.type ? 'context_menu-grid-selected' : ''}`}>
                      <div>
                        <div>
                          <span
                            className={`material-symbols-rounded ${questionType.inverted ? 'material-symbols-inv' : ''}`}>
                            {questionType.icon}
                          </span>
                          <h3>{questionType.name}</h3>
                        </div>
                        <span>{questionType.description}</span>
                      </div>
                    </label>
                  </Fragment>
                ))}
              </div>
            )}
          </div>
          {(question.type === 'text' ||
            question.type === 'textarea' ||
            question.type === 'number' ||
            question.type === 'email' ||
            question.type === 'tel') && (
            <>
              <div />
              <div className='actions_container actions_container-alt'>
                <div className='section-new_item-options'>
                  {(question.type === 'text' || question.type === 'textarea') && (
                    <>
                      <div className='actions-select'>
                        <div className='actions_series'>
                          <div
                            onClick={() =>
                              setQuestionSetting({
                                ...questionSetting,
                                minlength: true
                              })
                            }>
                            <div className='div-with-icon div-with-icon-pd'>
                              <span>Min length:</span>
                              {questionSetting.minlength ? (
                                <input
                                  id='minlength'
                                  type='number'
                                  defaultValue={question.meta?.textOptions?.minlength}
                                  onBlur={(e) => {
                                    handleNewQuestionTextOptions(e)
                                    setQuestionSetting({
                                      ...questionSetting,
                                      minlength: false
                                    })
                                  }}
                                  min={0}
                                  autoFocus
                                  placeholder='--'
                                />
                              ) : (
                                <span className='span-input'>
                                  {question.meta?.textOptions?.minlength !== undefined
                                    ? formatNum(question.meta.textOptions.minlength)
                                    : '--'}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className='actions-select'>
                        <div className='actions_series'>
                          <div
                            onClick={() =>
                              setQuestionSetting({
                                ...questionSetting,
                                maxlength: true
                              })
                            }>
                            <div className='div-with-icon div-with-icon-pd'>
                              <span>Max length:</span>
                              {questionSetting.maxlength ? (
                                <input
                                  id='maxlength'
                                  type='number'
                                  defaultValue={question.meta?.textOptions?.maxlength}
                                  onBlur={(e) => {
                                    handleNewQuestionTextOptions(e)
                                    setQuestionSetting({
                                      ...questionSetting,
                                      maxlength: false
                                    })
                                  }}
                                  min={0}
                                  autoFocus
                                  placeholder='--'
                                />
                              ) : (
                                <span className='span-input'>
                                  {question.meta?.textOptions?.maxlength !== undefined
                                    ? formatNum(question.meta.textOptions.maxlength)
                                    : '--'}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* <div className='actions-select'>
                        <div className='actions_series'>
                          <div onClick={handleNewQuestionSpellcheck}>
                            <div className='div-with-icon div-with-icon-pd'>
                              <span>Spell check:</span>
                              <input
                                id='spellcheck'
                                type='checkbox'
                                defaultChecked={
                                  question.meta?.textOptions?.spellcheck
                                }
                                onChange={(e) => {
                                  handleNewQuestionTextOptions(e)
                                  setQuestionSetting({
                                    ...questionSetting,
                                    spellcheck: false
                                  })
                                }}
                                hidden
                              />
                              <span className='span-input'>
                                {question.meta?.textOptions?.spellcheck
                                  ? 'Yes'
                                  : 'No'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div> */}
                    </>
                  )}
                  {question.type === 'number' && (
                    <>
                      <div className='actions-select'>
                        <div className='actions_series'>
                          <div
                            onClick={() =>
                              setQuestionSetting({
                                ...questionSetting,
                                unit: true
                              })
                            }>
                            <div className='div-with-icon div-with-icon-pd'>
                              <span>Units:</span>
                              {questionSetting.unit ? (
                                <input
                                  id='unit'
                                  defaultValue={question.meta?.numberOptions?.unit}
                                  onBlur={(e) => {
                                    handleNewQuestionNumberOptions(e, true)
                                    setQuestionSetting({
                                      ...questionSetting,
                                      unit: false
                                    })
                                  }}
                                  maxLength={10}
                                  autoFocus
                                  placeholder='--'
                                />
                              ) : (
                                <span className='span-input'>
                                  {question.meta?.numberOptions?.unit ?? '--'}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className='actions-select'>
                        <div className='actions_series'>
                          <div
                            onClick={() =>
                              setQuestionSetting({
                                ...questionSetting,
                                min: true
                              })
                            }>
                            <div className='div-with-icon div-with-icon-pd'>
                              <span>Min:</span>
                              {questionSetting.min ? (
                                <input
                                  id='min'
                                  type='number'
                                  defaultValue={question.meta?.numberOptions?.min}
                                  onBlur={(e) => {
                                    handleNewQuestionNumberOptions(e)
                                    setQuestionSetting({
                                      ...questionSetting,
                                      min: false
                                    })
                                  }}
                                  autoFocus
                                  placeholder='--'
                                />
                              ) : (
                                <span className='span-input'>
                                  {question.meta?.numberOptions?.min !== undefined
                                    ? formatNum(question.meta.numberOptions.min)
                                    : '--'}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className='actions-select'>
                        <div className='actions_series'>
                          <div
                            onClick={() =>
                              setQuestionSetting({
                                ...questionSetting,
                                max: true
                              })
                            }>
                            <div className='div-with-icon div-with-icon-pd'>
                              <span>Max:</span>
                              {questionSetting.max ? (
                                <input
                                  id='max'
                                  type='number'
                                  defaultValue={question.meta?.numberOptions?.max}
                                  onBlur={(e) => {
                                    handleNewQuestionNumberOptions(e)
                                    setQuestionSetting({
                                      ...questionSetting,
                                      max: false
                                    })
                                  }}
                                  autoFocus
                                  placeholder='--'
                                />
                              ) : (
                                <span className='span-input'>
                                  {question.meta?.numberOptions?.max !== undefined
                                    ? formatNum(question.meta.numberOptions.max)
                                    : '--'}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                  {question.type === 'email' && (
                    <>
                      <div className='actions-select'>
                        <div className='actions_series'>
                          <div
                            onClick={() =>
                              setQuestionSetting({
                                ...questionSetting,
                                minlength: true
                              })
                            }>
                            <div className='div-with-icon div-with-icon-pd'>
                              <span>Min length:</span>
                              {questionSetting.minlength ? (
                                <input
                                  id='minlength'
                                  type='number'
                                  defaultValue={question.meta?.emailOptions?.minlength}
                                  onBlur={(e) => {
                                    handleNewQuestionEmailOptions(e)
                                    setQuestionSetting({
                                      ...questionSetting,
                                      minlength: false
                                    })
                                  }}
                                  autoFocus
                                  placeholder='--'
                                />
                              ) : (
                                <span className='span-input'>
                                  {question.meta?.emailOptions?.minlength !== undefined
                                    ? formatNum(question.meta.emailOptions.minlength)
                                    : '--'}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className='actions-select'>
                        <div className='actions_series'>
                          <div
                            onClick={() =>
                              setQuestionSetting({
                                ...questionSetting,
                                maxlength: true
                              })
                            }>
                            <div className='div-with-icon div-with-icon-pd'>
                              <span>Max length:</span>
                              {questionSetting.maxlength ? (
                                <input
                                  id='maxlength'
                                  type='number'
                                  defaultValue={question.meta?.emailOptions?.maxlength}
                                  onBlur={(e) => {
                                    handleNewQuestionEmailOptions(e)
                                    setQuestionSetting({
                                      ...questionSetting,
                                      maxlength: false
                                    })
                                  }}
                                  autoFocus
                                  placeholder='--'
                                />
                              ) : (
                                <span className='span-input'>
                                  {question.meta?.emailOptions?.maxlength !== undefined
                                    ? formatNum(question.meta.emailOptions.maxlength)
                                    : '--'}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                  {question.type === 'tel' && (
                    <>
                      <div className='actions-select'>
                        <div className='actions_series'>
                          <div
                            onClick={() =>
                              setQuestionSetting({
                                ...questionSetting,
                                minlength: true
                              })
                            }>
                            <div className='div-with-icon div-with-icon-pd'>
                              <span>Min length:</span>
                              {questionSetting.minlength ? (
                                <input
                                  id='minlength'
                                  type='number'
                                  defaultValue={question.meta?.telOptions?.minlength}
                                  onBlur={(e) => {
                                    handleNewQuestionTelOptions(e)
                                    setQuestionSetting({
                                      ...questionSetting,
                                      minlength: false
                                    })
                                  }}
                                  autoFocus
                                  placeholder='--'
                                />
                              ) : (
                                <span className='span-input'>
                                  {question.meta?.telOptions?.minlength !== undefined
                                    ? formatNum(question.meta.telOptions.minlength)
                                    : '--'}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className='actions-select'>
                        <div className='actions_series'>
                          <div
                            onClick={() =>
                              setQuestionSetting({
                                ...questionSetting,
                                maxlength: true
                              })
                            }>
                            <div className='div-with-icon div-with-icon-pd'>
                              <span>Max length:</span>
                              {questionSetting.maxlength ? (
                                <input
                                  id='maxlength'
                                  type='number'
                                  defaultValue={question.meta?.telOptions?.maxlength}
                                  onBlur={(e) => {
                                    handleNewQuestionTelOptions(e)
                                    setQuestionSetting({
                                      ...questionSetting,
                                      maxlength: false
                                    })
                                  }}
                                  autoFocus
                                  placeholder='--'
                                />
                              ) : (
                                <span className='span-input'>
                                  {question.meta?.telOptions?.maxlength !== undefined
                                    ? formatNum(question.meta.telOptions.maxlength)
                                    : '--'}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                  <div className='actions-select'>
                    <div className='actions_series'>
                      <div
                        onClick={() =>
                          setQuestionSetting({
                            ...questionSetting,
                            placeholder: true
                          })
                        }>
                        <div className='div-with-icon div-with-icon-pd'>
                          <span>Hint:</span>
                          {questionSetting.placeholder ? (
                            <input
                              id='placeholder'
                              defaultValue={question.meta?.placeholder}
                              onBlur={(e) => {
                                handleNewQuestionPlaceholder(e)
                                setQuestionSetting({
                                  ...questionSetting,
                                  placeholder: false
                                })
                              }}
                              maxLength={50}
                              autoFocus
                              placeholder='--'
                            />
                          ) : (
                            <span className='span-input'>{question.meta?.placeholder ?? '--'}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
          {question.type === 'date' && (
            <>
              <div />
              <div className='actions_container actions_container-alt'>
                <div className='section-new_item-options'>
                  <div className='actions-select'>
                    <div className='actions_series'>
                      <div
                        onClick={() =>
                          setQuestionSetting({
                            ...questionSetting,
                            min: true
                          })
                        }>
                        <div className='div-with-icon div-with-icon-pd'>
                          <span>From:</span>
                          {questionSetting.min ? (
                            <input
                              id='min'
                              type='date'
                              defaultValue={
                                question.meta?.dateOptions?.min &&
                                setToCalendarDate(question.meta.dateOptions.min)
                              }
                              onBlur={(e) => {
                                handleNewQuestionDateOptions(e)
                                setQuestionSetting({
                                  ...questionSetting,
                                  min: false
                                })
                              }}
                              autoFocus
                              placeholder='--'
                            />
                          ) : (
                            <span className='span-input'>
                              {question.meta?.dateOptions?.min
                                ? parseDate(question.meta.dateOptions.min, 'medium_date')
                                : '--'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='actions-select'>
                    <div className='actions_series'>
                      <div
                        onClick={() =>
                          setQuestionSetting({
                            ...questionSetting,
                            max: true
                          })
                        }>
                        <div className='div-with-icon div-with-icon-pd'>
                          <span>To:</span>
                          {questionSetting.max ? (
                            <input
                              id='max'
                              type='date'
                              defaultValue={
                                question.meta?.dateOptions?.max &&
                                setToCalendarDate(question.meta.dateOptions.max)
                              }
                              onBlur={(e) => {
                                handleNewQuestionDateOptions(e)
                                setQuestionSetting({
                                  ...questionSetting,
                                  max: false
                                })
                              }}
                              autoFocus
                              placeholder='--'
                            />
                          ) : (
                            <span className='span-input'>
                              {question.meta?.dateOptions?.max
                                ? parseDate(question.meta.dateOptions.max, 'medium_date')
                                : '--'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
          {question.type === 'dropdown' && (
            <>
              <div />
              <div className='section-new_item-list'>
                {question.meta?.dropdownOptions?.values.map((option, index) => (
                  <Fragment key={`${index}`}>
                    <label htmlFor={`dropdown_option_${index}`}>{index + 1}.</label>
                    <input
                      id={`dropdown_option_${index}`}
                      value={option}
                      onChange={(e) => handleNewQuestionDropdownEdit(e, index)}
                      placeholder='Add option...'
                    />
                    <div className='option-actions'>
                      {question.meta!.dropdownOptions!.values.length > 1 && (
                        <>
                          <span
                            className={`material-symbols-rounded ${!index ? 'disabled' : ''}`}
                            onClick={() =>
                              handleMoveCheckbox(option, index, index - 1, {
                                name: 'dropdownOptions',
                                values: question.meta?.dropdownOptions?.values
                              })
                            }>
                            {index ? 'arrow_upward_alt' : 'check_indeterminate_small'}
                          </span>
                          <span
                            className={`material-symbols-rounded ${index === question.meta!.dropdownOptions!.values.length - 1 ? 'disabled' : ''}`}
                            onClick={() =>
                              handleMoveCheckbox(option, index, index + 1, {
                                name: 'dropdownOptions',
                                values: question.meta?.dropdownOptions?.values
                              })
                            }>
                            {index === question.meta!.dropdownOptions!.values.length - 1
                              ? 'check_indeterminate_small'
                              : 'arrow_downward_alt'}
                          </span>
                        </>
                      )}
                      <span
                        className='material-symbols-rounded error'
                        onClick={() => handleNewQuestionDropdownDelete(index)}>
                        close_small
                      </span>
                    </div>
                  </Fragment>
                ))}
                {isNewOptionField && (
                  <>
                    <label htmlFor='option'>
                      {question.meta?.dropdownOptions
                        ? question.meta.dropdownOptions.values.length + 1
                        : 1}
                      .
                    </label>
                    <input
                      id='option'
                      value={optionValue}
                      onChange={handleNewQuestionOptionChange}
                      onBlur={handleNewQuestionDropdownOptions}
                      placeholder='Add option...'
                    />
                    <div className='option-actions' />
                  </>
                )}
                <span
                  className={`material-symbols-rounded badge-rounded ${!optionValue ? 'disabled' : ''}`}
                  onClick={handleNewOption}>
                  add
                </span>
              </div>
            </>
          )}
          {question.type === 'checkbox' && (
            <>
              <div />
              <div className='section-new_item-list'>
                {question.meta?.checkboxOptions?.values.map((option, index) => (
                  <Fragment key={`${index}`}>
                    <label htmlFor={`checkbox_option_${index}`}>{index + 1}.</label>
                    <input
                      id={`checkbox_option_${index}`}
                      value={option}
                      onChange={(e) => handleNewQuestionCheckboxEdit(e, index)}
                      placeholder='Add option...'
                    />
                    <div className='option-actions'>
                      {question.meta!.checkboxOptions!.values.length > 1 && (
                        <>
                          <span
                            className={`material-symbols-rounded ${!index ? 'disabled' : ''}`}
                            onClick={() =>
                              handleMoveCheckbox(option, index, index - 1, {
                                name: 'checkboxOptions',
                                values: question.meta?.checkboxOptions?.values
                              })
                            }>
                            {index ? 'arrow_upward_alt' : 'check_indeterminate_small'}
                          </span>
                          <span
                            className={`material-symbols-rounded ${index === question.meta!.checkboxOptions!.values.length - 1 ? 'disabled' : ''}`}
                            onClick={() =>
                              handleMoveCheckbox(option, index, index + 1, {
                                name: 'checkboxOptions',
                                values: question.meta?.checkboxOptions?.values
                              })
                            }>
                            {index === question.meta!.checkboxOptions!.values.length - 1
                              ? 'check_indeterminate_small'
                              : 'arrow_downward_alt'}
                          </span>
                        </>
                      )}
                      <span
                        className='material-symbols-rounded error'
                        onClick={() => handleNewQuestionCheckboxDelete(index)}>
                        close_small
                      </span>
                    </div>
                  </Fragment>
                ))}
                {isNewOptionField && (
                  <>
                    <label htmlFor='option'>
                      {question.meta?.checkboxOptions
                        ? question.meta.checkboxOptions.values.length + 1
                        : 1}
                      .
                    </label>
                    <input
                      id='option'
                      value={optionValue}
                      onChange={handleNewQuestionOptionChange}
                      onBlur={handleNewQuestionCheckboxOptions}
                      placeholder='Add option...'
                    />
                    <div className='option-actions' />
                  </>
                )}
                <span
                  className={`material-symbols-rounded badge-rounded ${!optionValue ? 'disabled' : ''}`}
                  onClick={handleNewOption}>
                  add
                </span>
              </div>
              <div />
              <div className='actions_container actions_container-alt'>
                <div className='section-new_item-options'>
                  <div className='actions-select'>
                    <div className='actions_series'>
                      <div
                        onClick={() =>
                          setQuestionSetting({
                            ...questionSetting,
                            minOptions: true
                          })
                        }>
                        <div className='div-with-icon div-with-icon-pd'>
                          <span>Min selected:</span>
                          {questionSetting.minOptions ? (
                            <input
                              id='minOptions'
                              type='number'
                              defaultValue={question.meta?.checkboxOptions?.minOptions}
                              onBlur={(e) => {
                                handleNewQuestionSelectOptions(e)
                                setQuestionSetting({
                                  ...questionSetting,
                                  minOptions: false
                                })
                              }}
                              min={1}
                              max={question.meta?.checkboxOptions?.values.length}
                              autoFocus
                              placeholder='--'
                            />
                          ) : (
                            <span className='span-input'>
                              {question.meta?.checkboxOptions?.minOptions ?? '--'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='actions-select'>
                    <div className='actions_series'>
                      <div
                        onClick={() =>
                          setQuestionSetting({
                            ...questionSetting,
                            maxOptions: true
                          })
                        }>
                        <div className='div-with-icon div-with-icon-pd'>
                          <span>Max selected:</span>
                          {questionSetting.maxOptions ? (
                            <input
                              id='maxOptions'
                              type='number'
                              defaultValue={question.meta?.checkboxOptions?.maxOptions}
                              onBlur={(e) => {
                                handleNewQuestionSelectOptions(e)
                                setQuestionSetting({
                                  ...questionSetting,
                                  maxOptions: false
                                })
                              }}
                              min={1}
                              max={question.meta?.checkboxOptions?.values.length}
                              autoFocus
                              placeholder='--'
                            />
                          ) : (
                            <span className='span-input'>
                              {question.meta?.checkboxOptions?.maxOptions ?? '--'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
          {question.type === 'rating' && (
            <>
              <div />
              <div className='actions_container actions_container-alt'>
                <div className='section-new_item-options'>
                  <div className='actions-select'>
                    <div className='actions_series'>
                      <div
                        onClick={() =>
                          setQuestionSetting({
                            ...questionSetting,
                            max: true
                          })
                        }>
                        <div className='div-with-icon div-with-icon-pd'>
                          <span>Max rating:</span>
                          {questionSetting.max ? (
                            <input
                              id='max'
                              type='number'
                              defaultValue={question.meta?.ratingOptions?.length}
                              onBlur={(e) => {
                                handleNewQuestionRatingOptions(e)
                                setQuestionSetting({
                                  ...questionSetting,
                                  max: false
                                })
                              }}
                              min={0}
                              autoFocus
                              placeholder='--'
                            />
                          ) : (
                            <span className='span-input'>
                              {question.meta?.ratingOptions?.length ?? '--'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
          {validationError && (
            <>
              <div />
              <span className='error'>{validationError}</span>
            </>
          )}
          {/* {!!allQuestions.filter(
              (question) => question.type === 'number'
            ).length && (
              <div className='question-new_rule-list'>
                <h3>Conditions:</h3>
                <div>
                  <span>
                    (Add rule specifying when question should be
                    displayed i.e "Show if Question 1 &gt; 500".
                    Optional)
                  </span>
                  <label htmlFor='questionId'>
                    Show if question:
                  </label>
                  <select
                    id='questionId'
                    onChange={handleNewQuestionConditionSelect}>
                    <option value='' hidden>
                      Select from list...
                    </option>
                    {allQuestions
                      .filter(({ type }) => type)
                      .map((question, index) => (
                        <option
                          value={question.id}
                          key={index}
                          disabled={question.type !== 'number'}>
                          {question.statement}
                        </option>
                      ))}
                  </select>
                </div>
                <div>
                  <label htmlFor='condition'>Condition:</label>
                  <select
                    id='condition'
                    onChange={handleNewQuestionConditionSelect}>
                    <option value='' hidden>
                      Select from list...
                    </option>
                    <option value='<'>is less than</option>
                    <option value='>'>is greater than</option>
                  </select>
                </div>
                <div>
                  <label htmlFor='value'>Value:</label>
                  <input
                    id='value'
                    type='number'
                    onChange={handleNewQuestionConditionSelect}
                    placeholder='Enter number...'
                  />
                </div>
              </div>
            )} */}
          <div />
          <div className='form_actions'>
            <div className='div-with-icon' onClick={reset}>
              <span className='material-symbols-rounded'>close</span>
              <span>Cancel</span>
            </div>
            <button
              type='button'
              className='button-with-icon'
              onClick={() => save(question, section.id, questionIndex)}
              disabled={
                !question.statement ||
                !!validationError ||
                (question.type === 'dropdown' && !question.meta?.dropdownOptions?.values.length) ||
                (question.type === 'checkbox' &&
                  (!question.meta?.checkboxOptions?.values.length ||
                    (question.meta?.checkboxOptions.maxOptions !== undefined &&
                      question.meta?.checkboxOptions.maxOptions >
                        question.meta?.checkboxOptions.values.length))) ||
                (question.type === 'rating' && !question.meta?.ratingOptions?.length) ||
                !(questionIndex >= 0 && questionIndex < maxValue.current)
              }>
              <span className='material-symbols-rounded'>check</span>
              <span>Confirm</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
